import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { useSelector } from "react-redux";
import { Navigate, Link, useParams } from "react-router-dom";
import { Button, Input, Modal, Tag, message } from "antd";
import NewMiniDrawer from "../../../components/navMenu/menu-nav";
import MapaRota from "../../../components/MapaRota";
import LinhaDoTempoFrete from "../../../components/LinhaDoTempoFrete";
import freightService from "../../../service/freight.service";
import acompanhamentoApi from "../../../service/modules/acompanhamento.api";
import canhotoApi from "../../../service/modules/canhoto.api";
import {
  marcacaoDoFrete,
  CORES_DA_MARCACAO,
  ROTULOS_DA_MARCACAO,
} from "../../../util/freight/acompanhamento-marcacoes";
import {
  CORES_DO_ALERTA,
  detalheDoAlerta,
  ordenarAlertas,
  rotuloDoAlerta,
} from "../../../util/freight/alertas-do-frete";
import { translatorForTheStatusOfStoppingPoints } from "../../frete-copy/action/action";

/**
 * Classificações que a conferência automática grava. Só sobre uma delas faz sentido aprovar exceção:
 * `CONFERIDO` bateu dentro do raio e `EXCECAO_APROVADA` já foi aprovada.
 */
const PRECISA_DE_EXCECAO = [
  "FORA_DO_RAIO",
  "SEM_REFERENCIA",
  "PRECISAO_INSUFICIENTE",
  "SEM_LOCALIZACAO",
];

const ROTULO_DO_CANHOTO = {
  CONFERIDO: "Canhoto conferido",
  FORA_DO_RAIO: "Canhoto fora do raio da entrega",
  SEM_REFERENCIA: "Canhoto sem endereço de referência",
  PRECISAO_INSUFICIENTE: "Canhoto com localização imprecisa",
  SEM_LOCALIZACAO: "Canhoto sem localização",
  EXCECAO_APROVADA: "Exceção aprovada",
};

const COR_DO_CANHOTO = {
  CONFERIDO: "green",
  EXCECAO_APROVADA: "blue",
};

const MINIMO_DA_JUSTIFICATIVA = 10;

function paraMillis(valor) {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1e6);
  }
  const d = new Date(valor).getTime();
  return Number.isNaN(d) ? null : d;
}

function dataHora(valor) {
  const ms = paraMillis(valor);
  return ms ? new Date(ms).toLocaleString("pt-BR") : "—";
}

function statusDoPonto(ponto) {
  const bruto =
    ponto?.typeOperation === "UNLOADING"
      ? ponto?.unloadingStatus
      : ponto?.loadingStatus;
  const traduzido = translatorForTheStatusOfStoppingPoints(
    String(bruto || "").toUpperCase(),
  );
  return Array.isArray(traduzido) ? "—" : traduzido;
}

function endereco(ponto) {
  const partes = [
    [ponto?.logradouro, ponto?.number].filter(Boolean).join(", "),
    [ponto?.city, ponto?.uf].filter(Boolean).join(" - "),
  ].filter(Boolean);
  return partes.join(" · ") || "Endereço não informado";
}

function DetalheDoFrete() {
  const { id } = useParams();
  const logado = useSelector((state) => state.usuarioLogado);

  const [frete, setFrete] = useState(null);
  const [pontos, setPontos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [pontoEmExcecao, setPontoEmExcecao] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!id) return undefined;

    let cancelado = false;
    setCarregando(true);
    setErro(null);

    acompanhamentoApi
      .buscarFrete(id)
      .then((resposta) => {
        if (cancelado) return;
        setFrete(resposta?.data?.data || null);
      })
      .catch((e) => {
        if (cancelado) return;
        setErro(
          e?.response?.status === 404
            ? "Frete não encontrado."
            : "Não foi possível carregar este frete.",
        );
        console.error("Erro ao carregar o frete:", e);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;

    const cancelar = freightService.subscribeStoppingPointsByFreight(
      id,
      (snap) =>
        setPontos(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
      (e) => console.error("Erro ao carregar os pontos de parada:", e),
    );

    return () => cancelar && cancelar();
  }, [id]);

  const marcacao = useMemo(
    () => (frete ? marcacaoDoFrete(frete) : null),
    [frete],
  );

  const alertas = useMemo(() => ordenarAlertas(frete?.alertas), [frete]);

  const aprovarExcecao = async () => {
    if (!pontoEmExcecao) return;

    setEnviando(true);
    try {
      await canhotoApi.aprovarExcecao({
        freightId: id,
        pointId: pontoEmExcecao.id,
        justification: justificativa.trim(),
      });
      message.success("Exceção aprovada.");
      setPontoEmExcecao(null);
      setJustificativa("");
    } catch (e) {
      const doBackend = e?.response?.data?.message;
      message.error(doBackend || "Não foi possível aprovar a exceção.");
      console.error("Erro ao aprovar exceção de canhoto:", e);
    } finally {
      setEnviando(false);
    }
  };

  const conteudo = (
    <div className="detalhe">
      <header className="detalhe__cabecalho">
        <div>
          <Link className="detalhe__voltar" to="/freightsDetail">
            ‹ Acompanhamento de entregas
          </Link>
          <h1>
            Frete {frete?.numberSerial ?? id}
            {marcacao && (
              <Tag color={CORES_DA_MARCACAO[marcacao]}>
                {ROTULOS_DA_MARCACAO[marcacao]}
              </Tag>
            )}
          </h1>
          <p className="detalhe__resumo">
            {frete?.shipper?.name || "Embarcador não informado"} ·{" "}
            {frete?.driver?.name ||
              frete?.driver?.driverName ||
              "Motorista não informado"}
            {frete?.plate ? ` · ${frete.plate}` : ""} ·{" "}
            {frete?.firstDelivery?.city || "—"} -{" "}
            {frete?.firstDelivery?.uf || "—"} →{" "}
            {frete?.lastDelivery?.city || "—"} -{" "}
            {frete?.lastDelivery?.uf || "—"}
          </p>
        </div>
      </header>

      {erro ? (
        <p className="detalhe__erro">{erro}</p>
      ) : carregando ? (
        <p className="detalhe__vazio">Carregando o frete...</p>
      ) : (
        <>
          <section className="detalhe__mapa">
            <h2>Trajeto percorrido</h2>
            <MapaRota frete={frete} />
          </section>

          <section className="detalhe__bloco detalhe__alertas">
            <h2>Ocorrências</h2>
            {!alertas.length ? (
              <p className="detalhe__vazio">
                Nenhuma ocorrência ativa neste frete.
              </p>
            ) : (
              <ul className="detalhe__lista-de-alertas">
                {alertas.map((alerta) => (
                  <li key={alerta.chave}>
                    <Tag color={CORES_DO_ALERTA[alerta.chave]}>
                      {rotuloDoAlerta(alerta)}
                    </Tag>
                    <span>{detalheDoAlerta(alerta)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="detalhe__colunas">
            <section className="detalhe__bloco">
              <h2>Linha do tempo</h2>
              <LinhaDoTempoFrete frete={frete} />
            </section>

            <section className="detalhe__bloco">
              <h2>Pontos de parada</h2>
              {!pontos.length ? (
                <p className="detalhe__vazio">
                  Este frete não tem pontos de parada.
                </p>
              ) : (
                <ol className="detalhe__pontos">
                  {pontos.map((ponto) => {
                    const canhoto = String(ponto.receiptStatus || "");
                    const podeAprovar = PRECISA_DE_EXCECAO.includes(canhoto);

                    return (
                      <li key={ponto.id} className="detalhe__ponto">
                        <div className="detalhe__ponto-topo">
                          <strong>
                            {ponto.stop_order}.{" "}
                            {ponto.name || "Ponto de parada"}
                          </strong>
                          <span className="detalhe__ponto-tipo">
                            {ponto.typeOperation === "UNLOADING"
                              ? "Entrega"
                              : "Coleta"}
                          </span>
                        </div>
                        <span className="detalhe__ponto-endereco">
                          {endereco(ponto)}
                        </span>
                        <span className="detalhe__ponto-status">
                          {statusDoPonto(ponto)}
                          {ponto.date_operation
                            ? ` · ${ponto.date_operation}`
                            : ""}
                        </span>

                        {canhoto && (
                          <div className="detalhe__canhoto">
                            <Tag color={COR_DO_CANHOTO[canhoto] || "orange"}>
                              {ROTULO_DO_CANHOTO[canhoto] || canhoto}
                            </Tag>
                            {Number.isFinite(
                              Number(ponto.receiptDistanceMeters),
                            ) && (
                              <span>
                                {Math.round(ponto.receiptDistanceMeters)} m do
                                endereço
                              </span>
                            )}
                            {ponto.receiptCheckedAt && (
                              <span>
                                Conferido em {dataHora(ponto.receiptCheckedAt)}
                              </span>
                            )}
                            {ponto.receiptException && (
                              <span className="detalhe__canhoto-excecao">
                                “{ponto.receiptException.justification}” —{" "}
                                {dataHora(ponto.receiptException.approvedAt)}
                              </span>
                            )}
                            {podeAprovar && (
                              <Button
                                size="small"
                                onClick={() => {
                                  setPontoEmExcecao(ponto);
                                  setJustificativa("");
                                }}
                              >
                                Aprovar exceção
                              </Button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>
        </>
      )}

      <Modal
        title="Aprovar exceção do canhoto"
        open={Boolean(pontoEmExcecao)}
        onCancel={() => setPontoEmExcecao(null)}
        onOk={aprovarExcecao}
        okText="Aprovar"
        cancelText="Cancelar"
        confirmLoading={enviando}
        okButtonProps={{
          disabled: justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA,
        }}
      >
        <p className="detalhe__modal-texto">
          A distância medida continua guardada junto da aprovação. Escreva por
          que a divergência é aceitável — é o que responde a pergunta meses
          depois, numa disputa de carga.
        </p>
        <Input.TextArea
          rows={4}
          maxLength={500}
          showCount
          placeholder="Ex.: recebedor orientou a descarregar no pátio anexo, a 1,8 km do endereço cadastrado."
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
        />
      </Modal>
    </div>
  );

  return logado > 0 ? (
    <NewMiniDrawer divOpen={conteudo} />
  ) : (
    <Navigate to="/login" />
  );
}

export default DetalheDoFrete;
